/* eslint-disable @typescript-eslint/naming-convention */
import {
  PaletteColor,
  PaletteColorOptions,
  PaletteOptions,
  darken,
  lighten,
} from '@mui/material';
import { colors } from 'styles/colors';

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      primaryDark: string;
      cyan: string;
      green: string;
      magenta: string;
      red: string;
      orange: string;
      yellow: string;
      blue: string;
      navy: string;
      navyDark: string;
      navyLight: string;
    };
  }

  interface ThemeOptions {
    colors?: {
      primaryDark: string;
      cyan: string;
      green: string;
      magenta: string;
      red: string;
      orange: string;
      yellow: string;
      blue: string;
      navy: string;
      navyDark: string;
      navyLight: string;
    };
  }

  interface TypeBackground {
    default: string;
    paper: string;
    midGray: string;
    lightGray: string;
    errorLight: string;
    disabled: string;
  }

  interface Palette {
    neutral: PaletteColor;
    primaryDark: PaletteColor;
    cyan: PaletteColor;
    green: PaletteColor;
    magenta: PaletteColor;
    red: PaletteColor;
    orange: PaletteColor;
    yellow: PaletteColor;
    blue: PaletteColor;
    navy: PaletteColor;
    navyDark: PaletteColor;
    navyLight: PaletteColor;
    state: {
      hover: string;
      selected: string;
    };
    infoSuccess: PaletteColor;
    infoNeutral: PaletteColor;
  }

  interface PaletteOptions {
    neutral?: PaletteColorOptions;
    primaryDark?: PaletteColorOptions;
    cyan?: PaletteColorOptions;
    green?: PaletteColorOptions;
    magenta?: PaletteColorOptions;
    red?: PaletteColorOptions;
    orange?: PaletteColorOptions;
    yellow?: PaletteColorOptions;
    blue?: PaletteColorOptions;
    navy?: PaletteColorOptions;
    state?: {
      hover: string;
      selected: string;
    };
    infoNeutral?: PaletteColorOptions;
    infoSuccess?: PaletteColorOptions;
    border?: {
      disabledGray: string;
    };
  }
}

const TONAL_OFFSET = 0.1;

export const calculateColor = (color: string) => ({
  light: lighten(color, TONAL_OFFSET),
  main: color,
  dark: darken(color, TONAL_OFFSET),
});

export const palette: PaletteOptions = {
  primary: {
    main: colors.primary,
    contrastText: colors.type.copy,
  },
  secondary: {
    main: colors.secondary,
    dark: colors.secondaryDark,
    light: colors.secondaryLight,
  },
  success: {
    main: colors.tertiary.green,
  },
  error: {
    main: colors.status.error,
  },
  info: {
    main: colors.hightlight.light,
    dark: colors.hightlight.dark,
  },
  infoNeutral: {
    main: colors.infoNeutral.main,
  },
  infoSuccess: {
    main: colors.infoSuccess.main,
  },
  text: {
    primary: colors.type.copy,
    secondary: colors.type.copy,
    disabled: colors.type.disabled,
  },
  background: {
    default: colors.background.pageBackground,
    paper: colors.background.componentBackground,
    midGray: colors.background.midGray,
    lightGray: colors.background.lightGray,
    errorLight: colors.background.error,
    disabled: colors.background.disabled,
  },
  neutral: {
    main: colors.background.lightGray,
    dark: colors.background.midGray,
    contrastText: colors.background.darkGray,
  },
  divider: '#DCDCDC',
  primaryDark: calculateColor(colors.primaryDark),
  cyan: calculateColor(colors.tertiary.cyan),
  green: calculateColor(colors.tertiary.green),
  magenta: calculateColor(colors.tertiary.magenta),
  red: calculateColor(colors.tertiary.red),
  orange: calculateColor(colors.tertiary.orange),
  yellow: calculateColor(colors.tertiary.yellow),
  blue: calculateColor(colors.tertiary.blue),
  navy: {
    main: colors.tertiary.navy,
    dark: colors.tertiary.navyDark,
    light: colors.tertiary.navyLight,
  },
  state: {
    hover: colors.state.hover,
    selected: colors.state.selected,
  },
  tonalOffset: TONAL_OFFSET,
  border: {
    disabledGray: colors.border.disabledGray,
  },
};
